import React, { useState } from 'react';
import { Col, Form, Row, UploadFile } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { handleFormErrors } from '../../common/errorHelpers';
import { getFormFieldData } from '../../common/filterHelpers';
import { SearchFormFieldEnum } from '../../common/searchFormField.types';
import { DetailsPageHeader } from '../../components/DetailsPageHeader';
import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';

import {
  mapErrorFieldsToFormLabels,
  mapPreferredStartDateToRequest,
  normalizeOptionalText,
  ProjectDataForm,
} from './ProjectDataForm';
import { useAddProject } from './useAddProject';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

export const AddProjectPage: React.FC = () => {
  const [form] = Form.useForm();
  const addProject = useAddProject();
  const navigate = useNavigate();

  const [attachments, setAttachments] = useState<UploadFile[]>([]);

  const handleSave = async () => {
    let formFieldsValue;

    try {
      formFieldsValue = await form.validateFields();
    } catch {
      return;
    }

    const formValues = {
      ...formFieldsValue,
      createDate: dayjs(),
      externalId: formFieldsValue.externalId ?? null,
      preferredStartDate: mapPreferredStartDateToRequest(formFieldsValue.preferredStartDate),
      clientHourlyRateMin: formFieldsValue.clientHourlyRateMin,
      clientHourlyRateMax: formFieldsValue.clientHourlyRateMax,
      candidateHourlyRateMin: formFieldsValue.candidateHourlyRateMin,
      candidateHourlyRateMax: formFieldsValue.candidateHourlyRateMax,
      hourlyRateNote: normalizeOptionalText(formFieldsValue.hourlyRateNote),
      travelRequired: formFieldsValue.travelRequired ?? false,
      internal: formFieldsValue.internal ?? false,
      contractTypes:
        getFormFieldData(SearchFormFieldEnum.ContractTypes).toPayloadValueFormatter?.(
          formFieldsValue[SearchFormFieldEnum.ContractTypes]
        ) ?? [],
    };

    const formData = new FormData();

    formData.append('request', new Blob([JSON.stringify(formValues)], { type: 'application/json' }));

    for (const attachment of attachments) {
      if (attachment.originFileObj instanceof Blob) {
        formData.append('attachments', attachment.originFileObj);
      }
    }

    const { data } = await addProject.mutateAsync(
      { data: formData },
      {
        onError: (error) => {
          handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
        },
      }
    );
    navigate(pathToUrl(PageRoutesEnum.projectsEdit, { id: data.uuid }));
  };

  const handleCancel = () => navigate(-1);

  return (
    <MainContainer>
      <DetailsPageHeader title="Add project" onSave={handleSave} onCancel={handleCancel} />
      <Row gutter={[15, 15]}>
        <Col xs={24}>
          <ProjectDataForm form={form} attachments={attachments} onAttachmentsChange={setAttachments} />
        </Col>
      </Row>
    </MainContainer>
  );
};
